import React, { useState, useEffect } from 'react';
import compose from 'lodash/fp/compose';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { withTranslation, withAccount, withUserPreferences, withMsgInfo } from '../hoc';
import { Input, Button, Form } from '../common/AuthFormItems';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { OtpBody, OtpDivChild, OtpDivImage } from './style';

const SpacedButton = styled(Button)`
  margin-top: 2%;
  margin-bottom: 2%;
`;


const OtpInfoText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
  text-align: center;
  padding-right: 60px;
  padding-left: 0px;
`;

const OtpDestination = styled.span`
  font-weight: bold;
  color: #333;
`;

// Modern styled components for delivery method selector
const DeliveryMethodContainer = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const DeliveryMethodTitle = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 16px;
  text-align: center;
  letter-spacing: -0.2px;
`;

const DeliveryOptionsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DeliveryOption = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  border: 2px solid ${props => props.selected ? '#4a90d9' : '#e8e8e8'};
  border-radius: 12px;
  background: ${props => props.selected ? '#f0f7ff' : '#fafafa'};
  color: ${props => props.selected ? '#1a1a1a' : '#666666'};
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 15px;
  font-weight: 500;
  min-height: 120px;
  box-shadow: ${props => props.selected 
    ? '0 4px 16px rgba(74, 144, 217, 0.15)' 
    : '0 2px 4px rgba(0, 0, 0, 0.02)'};

  &:hover {
    transform: translateY(-2px);
    border-color: ${props => props.selected ? '#4a90d9' : '#c0c0c0'};
    box-shadow: ${props => props.selected 
      ? '0 6px 20px rgba(74, 144, 217, 0.2)' 
      : '0 4px 12px rgba(0, 0, 0, 0.06)'};
    background: ${props => props.selected ? '#e8f3ff' : '#f5f5f5'};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 32px;
    height: 32px;
    margin-bottom: 12px;
    stroke-width: 1.5;
  }

  ${props => props.selected && `
    &::before {
      content: '';
      position: absolute;
      top: 8px;
      right: 8px;
      width: 20px;
      height: 20px;
      background: #4a90d9;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    &::after {
      content: '✓';
      position: absolute;
      top: 8px;
      right: 8px;
      width: 20px;
      height: 20px;
      color: white;
      font-size: 12px;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
    }
  `}
`;

const OptionLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
  color: ${props => props.selected ? '#1a1a1a' : '#666666'};
  transition: color 0.25s ease;
`;

const MaskedDestination = styled.span`
  font-size: 13px;
  color: ${props => props.selected ? '#4a90d9' : '#999999'};
  font-weight: 500;
  text-align: center;
  line-height: 1.4;
  transition: color 0.25s ease;
`;

const DeliveryMethodInfo = styled.p`
  font-size: 13px;
  color: #666666;
  margin-top: 14px;
  text-align: center;
  line-height: 1.5;
  padding: 0 8px;
`;

const SendOTPButton = styled(Button)`
  margin-top: 16px;
  width: 100%;
`;

const OTPSentMessage = styled.div`
  padding: 12px 16px;
  background: #e3f2fd;
  border-left: 4px solid #4a90d9;
  border-radius: 8px;
  margin-bottom: 20px;
  margin-top: 10px;
  
  p {
    margin: 0;
    color: #00000;
    font-size: 14px;
    font-weight: 500;
  }
`;

const EmailIcon = ({ color = 'currentColor' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke={color}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const PhoneIcon = ({ color = 'currentColor' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke={color}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
  </svg>
);

const OTPActionButtons = ({ isLoading, countdown, t }) => {
  return (
    <React.Fragment>
      <Button type="submit" name={t('Submit')} isLoading={isLoading} />
    </React.Fragment>
  );
};

const OTP = (props) => {
  const { account } = props;
  const navigate = useNavigate();
  const { isLoading, user } = account;
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpPhoneNumber, setOtpPhoneNumber] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState(null);
  const [hasPhoneNumber, setHasPhoneNumber] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  // Set language from localStorage when component mounts
  useEffect(() => {
    const preferredLanguage = localStorage.getItem('preferred_language') || 'eng';
    i18n.changeLanguage(preferredLanguage);
  }, [i18n]);

  // Redirect to login if user_id is not present and get OTP destination info
  useEffect(() => {
    const user_id = localStorage.getItem('user_id');
    if (!user_id) {
      navigate('/login');
      return;
    }
    
    // Get email and phone number for display
    const email = localStorage.getItem('otp_email') || '';
    const phoneNumber = localStorage.getItem('otp_phone_number');
    const phoneCountryCode = localStorage.getItem('otp_phone_country_code');
    
    setOtpEmail(email);
    
    // Only show phone option if both phone number and country code exist
    if (phoneNumber && phoneCountryCode) {
      const formattedPhone = `${phoneCountryCode} ${phoneNumber}`;
      setOtpPhoneNumber(formattedPhone);
      setHasPhoneNumber(true);
    } else {
      setHasPhoneNumber(false);
      setDeliveryMethod('email');
      setOtpSent(true);
    }
  }, []);

  // Countdown timer logic
  useEffect(() => {
    if (!otpSent) return;
    
    const countdownInterval = setInterval(() => {
      if (countdown === 0) {
        clearInterval(countdownInterval);
        setIsResendDisabled(false);
      } else {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [countdown, otpSent]);

  // Mask email
  const maskEmail = (email) => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 2) {
      return `${localPart[0]}***@${domain}`;
    }
    return `${localPart[0]}***${localPart[localPart.length - 1]}@${domain}`;
  };

  // Mask phone number 
  const maskPhoneNumber = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length <= 4) return phone;
    const lastFour = digits.slice(-4);
    const countryCode = phone.match(/^\+\d+/)?.[0] || '';
    return `${countryCode} ***${lastFour}`;
  };

  // Handle sending OTP for the first time
  const handleSendOTP = async () => {
    if (!deliveryMethod) {
      dispatch(
        props.setMsgInfo({
          success: false,
          msg: [t('Please select how you want to receive the OTP')],
        })
      );
      return;
    }

    const { resendOTP, setMsgInfo } = props;
    const email = localStorage.getItem('otp_email');
    const user_id = localStorage.getItem('user_id');

    try {
      setSendingOTP(true);
      await resendOTP({ email, user_id, delivery_method: deliveryMethod });
      
      let successMessage = t('TOTP sent! Please check your mail.');
      if (deliveryMethod === 'phone') {
        successMessage = t('TOTP sent! Please check your phone.');
      }
      
      dispatch(
        setMsgInfo({
          success: true,
          msg: [successMessage],
        })
      );
      
      setOtpSent(true);
      setIsResendDisabled(true);
      setCountdown(60);
    } catch (error) {
      console.error('Error sending OTP:', error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: [t('Failed to send OTP. Please try again.')],
        })
      );
    } finally {
      setSendingOTP(false);
    }
  };

  const handleResendClick = async () => {
    const { resendOTP, setMsgInfo } = props;
    const email = localStorage.getItem('otp_email');
    const user_id = localStorage.getItem('user_id');

    try {
      await resendOTP({ email, user_id, delivery_method: deliveryMethod });
      
      let successMessage = t('TOTP Resent please check your mail.');
      if (deliveryMethod === 'phone') {
        successMessage = t('TOTP Resent please check your phone.');
      }
      
      dispatch(
        setMsgInfo({
          success: true,
          msg: [successMessage],
        })
      );
      setIsResendDisabled(true);
      setCountdown(60);
    } catch (error) {
      console.error('Error resending OTP:', error);
    }
  };

  const onSubmit = (e, redirectPath) => {
    e.preventDefault();
    
    if (!otpSent) {
      dispatch(
        props.setMsgInfo({
          success: false,
          msg: [t('Please send OTP first before submitting')],
        })
      );
      return;
    }
    
    const { validateOTP } = props;
    if (isLoading) return;
    const email = localStorage.getItem('otp_email');
    const user_id = localStorage.getItem('user_id');
    const browser_id = localStorage.getItem('browserId');
    validateOTP({ otp, email, user_id, browser_id }, redirectPath);
  };

  const onChange = (e) => {
    setOtp(e.target.value);
  };

  // Handle delivery method change
  const handleDeliveryMethodChange = (method) => {
    if (!otpSent) {
      setDeliveryMethod(method);
    }
  };

  // Render delivery method selector
  const renderDeliveryMethodSelector = () => {
    if (!hasPhoneNumber) {
      return (
        <OtpInfoText>
          {t('OTP will be sent to')}:{' '}
          <OtpDestination>{maskEmail(otpEmail)}</OtpDestination>
        </OtpInfoText>
      );
    }

    return (
      <DeliveryMethodContainer>
        <DeliveryMethodTitle>
          {otpSent 
            ? t('OTP sent via') 
            : t('Choose how to receive your OTP')}
        </DeliveryMethodTitle>
        <DeliveryOptionsWrapper>
          <DeliveryOption
            type="button"
            selected={deliveryMethod === 'email'}
            onClick={() => handleDeliveryMethodChange('email')}
            disabled={otpSent}
          >
            <EmailIcon color={deliveryMethod === 'email' ? '#4a90d9' : '#999999'} />
            <OptionLabel selected={deliveryMethod === 'email'}>
              {t('Email')}
            </OptionLabel>
            <MaskedDestination selected={deliveryMethod === 'email'}>
              {maskEmail(otpEmail)}
            </MaskedDestination>
          </DeliveryOption>
          
          <DeliveryOption
            type="button"
            selected={deliveryMethod === 'phone'}
            onClick={() => handleDeliveryMethodChange('phone')}
            disabled={otpSent}
          >
            <PhoneIcon color={deliveryMethod === 'phone' ? '#4a90d9' : '#999999'} />
            <OptionLabel selected={deliveryMethod === 'phone'}>
              {t('SMS')}
            </OptionLabel>
            <MaskedDestination selected={deliveryMethod === 'phone'}>
              {maskPhoneNumber(otpPhoneNumber)}
            </MaskedDestination>
          </DeliveryOption>
        </DeliveryOptionsWrapper>
        
        {!otpSent && deliveryMethod && (
          <DeliveryMethodInfo>
            {deliveryMethod === 'email' && t('A verification code will be sent to your email address')}
            {deliveryMethod === 'phone' && t('A verification code will be sent via SMS to your phone')}
          </DeliveryMethodInfo>
        )}
        
        {!otpSent && (
          <SendOTPButton
            type="button"
            name={t('Send OTP')}
            isLoading={sendingOTP}
            onClick={handleSendOTP}
            actionButton
          />
        )}
        
        {otpSent && (
          <OTPSentMessage>
            <p>
              {deliveryMethod === 'email' 
                ? t('OTP has been sent to your email') 
                : t('OTP has been sent to your phone')}
            </p>
          </OTPSentMessage>
        )}
      </DeliveryMethodContainer>
    );
  };


  return (
    <>
      <OtpBody>
        <OtpDivImage></OtpDivImage>

        <OtpDivChild>
          <Form
            title={t('Please enter your one-time code (TOTP) here:')}
            onSubmit={(e) => onSubmit(e)}
            renderActionButtons={() => (
              <OTPActionButtons
                isLoading={isLoading}
                countdown={countdown}
                t={t}
              />
            )}
            render={() => (
              <React.Fragment>
                {renderDeliveryMethodSelector()}
                
                {otpSent && (
                  <>
                    <Input
                      icon="envelope"
                      onChange={onChange}
                      value={otp}
                      name="otp"
                      label="Otp"
                      placeholder={t('TOTP')}
                      required
                    />
                    <div>
                      {countdown <= 0 ? (
                        <SpacedButton
                          type="button"
                          name={t('Resend TOTP')}
                          isLoading={isLoading}
                          onClick={handleResendClick}
                          disabled={isResendDisabled}
                        />
                      ) : (
                        <p>{t('You can resend TOTP in {{countdown}} seconds', { countdown })}</p>
                      )}
                    </div>
                  </>
                )}
              </React.Fragment>
            )}
          />
        </OtpDivChild>
      </OtpBody>
    </>
  );
};

export default compose(withAccount, withTranslation, withUserPreferences, withMsgInfo)(OTP);